import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, useClientOptions } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, FilterBar, DataTable, StatusBadge, ErrorBox, Loading } from "@/components/common/ui-bits";
import { FormDialog } from "@/components/common/FormDialog";
import { CommentsBox } from "@/components/common/CommentsBox";
import { DocumentsBox } from "@/components/common/DocumentsBox";
import { fmtDateTime, optionList, PRIORITIES, SUPPORT_STATUSES } from "@/lib/format";

function RequestSheet({ id, onClose, onChanged }) {
  const { user } = useAuth();
  const internal = user.role !== "client";
  const { data: s, reload, setData } = useApi(id ? `/support/${id}` : null);
  const update = async (patch) => {
    try { const r = await api.patch(`/support/${id}`, patch); setData(r.data); onChanged(); toast.success("Solicitud actualizada"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="support-sheet">
        {!s || s.id !== id ? <Loading /> : <>
          <SheetHeader className="mb-4 text-left"><SheetTitle className="font-display">{s.subject}</SheetTitle>
            <SheetDescription>{s.number} · {s.client_name} · {fmtDateTime(s.created_at)}</SheetDescription></SheetHeader>
          <div className="mb-4 flex flex-wrap gap-2">
            {internal ? <>
              <select className="field-input w-48" value={s.status} onChange={(e) => update({ status: e.target.value })} data-testid="support-status-select" aria-label="Estado">{SUPPORT_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}</select>
              <select className="field-input w-36" value={s.priority} onChange={(e) => update({ priority: e.target.value })} data-testid="support-priority-select" aria-label="Prioridad">{PRIORITIES.map((x) => <option key={x} value={x}>{x}</option>)}</select>
            </> : <><StatusBadge status={s.status} /><StatusBadge status={s.priority} /></>}
          </div>
          <p className="mb-6 whitespace-pre-wrap text-sm text-slate-700">{s.description}</p>
          <h4 className="mb-2 text-sm font-bold">Comentarios</h4>
          <CommentsBox testid="support-comments" comments={s.comments} allowInternal={internal} onAdd={async (text, isInt) => { await api.post(`/support/${id}/comments`, { text, internal: isInt }); reload(); }} />
          <h4 className="mb-2 mt-6 text-sm font-bold">Archivos</h4>
          <DocumentsBox clientId={s.client_id} entityType="solicitud" entityId={s.id} testid="support-files" />
        </>}
      </SheetContent>
    </Sheet>
  );
}

export default function Support() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const internal = user.role !== "client";
  const base = internal ? "/solicitudes" : "/portal/solicitudes";
  const [f, setF] = useState({});
  const { data, loading, error, reload } = useApi("/support", f);
  const clients = useClientOptions();
  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState(id || null);

  const fields = [
    ...(internal ? [{ name: "client_id", label: "Cliente", type: "select", options: clients, required: true }] : []),
    { name: "subject", label: "Asunto", required: true, minLength: 3, full: true },
    { name: "priority", label: "Prioridad", type: "select", options: optionList(PRIORITIES), default: "Normal", required: true },
    { name: "description", label: "Descripción", type: "textarea", required: true, minLength: 5 },
  ];
  return (
    <div>
      <PageHeader title="Solicitudes de soporte" subtitle={internal ? "Solicitudes de clientes." : "Cree una solicitud y siga su estado."}
        actions={<Button onClick={() => setOpen(true)} data-testid="new-support-button"><Plus className="mr-2 h-4 w-4" />Nueva solicitud</Button>} />
      <FilterBar values={f} onChange={setF} testid="support-filters" filters={[{ key: "status", label: "Estado", options: optionList(SUPPORT_STATUSES) }, ...(internal ? [{ key: "client_id", label: "Cliente", options: clients }] : [])]} />
      <ErrorBox text={error} />
      <DataTable testid="support-table" loading={loading} rows={data} onRowClick={(r) => setOpenId(r.id)} empty="No hay solicitudes."
        columns={[{ key: "number", label: "Número", render: (r) => <span className="num text-xs">{r.number}</span> },
          { key: "subject", label: "Asunto", render: (r) => <span className="font-medium text-slate-900">{r.subject}{internal && <span className="block text-xs font-normal text-slate-500">{r.client_name}</span>}</span> },
          { key: "priority", label: "Prioridad", render: (r) => <StatusBadge status={r.priority} /> },
          { key: "created_at", label: "Creada", render: (r) => fmtDateTime(r.created_at) },
          { key: "status", label: "Estado", render: (r) => <StatusBadge status={r.status} /> }]} />
      <RequestSheet id={openId} onClose={() => { setOpenId(null); if (id) navigate(base); }} onChanged={reload} />
      <FormDialog open={open} onOpenChange={setOpen} title="Nueva solicitud" testid="support-form" fields={fields} initial={{ priority: "Normal" }}
        onSubmit={async (v) => { await api.post("/support", v); toast.success("Solicitud creada"); reload(); }} />
    </div>
  );
}
